import { useState, useMemo } from "react";
import { Modal, Input, Switch, Tooltip, Skeleton, Empty } from "antd";
import Swal from "sweetalert2";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  AppstoreOutlined, SearchOutlined, CheckCircleOutlined, StopOutlined,
} from "@ant-design/icons";
import { isSuperAdmin } from "../utils/role";
import {
  useServices, useCreateService, useUpdateService,
  useToggleServiceStatus, useDeleteService,
} from "../hooks/useServices";

/**
 * Manage Services — the master list of lead sources.
 *
 * The slug is the contract the public website forms post against
 * (request-demo, contact-us), which is why it is shown in the table: whoever
 * wires a new form needs to know the exact string. It is derived by the backend
 * from the name and never sent back on update — changing it would break every
 * lead and form already pointing at it.
 */

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

/** Preview only — the backend is what actually derives the stored slug. */
const slugify = (name) =>
  String(name || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const thStyle = {
  padding: "14px 16px", textAlign: "left", fontSize: 12, fontWeight: 700,
  color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px", whiteSpace: "nowrap",
};
const tdStyle = { padding: "14px 16px", fontSize: 13, color: "#374151", verticalAlign: "middle" };

const EMPTY_FORM = { name: "", description: "", status: 1 };

/* ── Add / Edit modal ─────────────────────────────────────────────────────── */
const ServiceFormModal = ({ service, onClose }) => {
  const isEdit = Boolean(service?.id);
  const [form, setForm] = useState(() =>
    isEdit
      ? { name: service.name || "", description: service.description || "", status: service.status ?? 1 }
      : { ...EMPTY_FORM },
  );
  const [errors, setErrors] = useState({});

  const createMutation = useCreateService();
  const updateMutation = useUpdateService();
  const saving = createMutation.isPending || updateMutation.isPending;

  /* On edit the stored slug is the truth; on create it is a live preview of what
     the backend will derive. */
  const slugPreview = isEdit ? service.slug : slugify(form.name);

  const handleSubmit = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Name is required";
    setErrors(next);
    if (Object.keys(next).length) return;

    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      status: form.status,
    };
    const mutation = isEdit ? updateMutation : createMutation;
    const args = isEdit ? { id: service.id, ...payload } : payload;
    mutation.mutate(args, { onSuccess: onClose });
  };

  const label = { fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 6 };

  return (
    <Modal
      open
      title={<span style={{ fontWeight: 700 }}>{isEdit ? "Edit service" : "Add service"}</span>}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={saving ? "Saving…" : "Save"}
      okButtonProps={{ loading: saving }}
      cancelButtonProps={{ disabled: saving }}
      destroyOnClose
      width={460}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 4 }}>
        <div>
          <label style={label}>Name <span style={{ color: "#dc2626" }}>*</span></label>
          <Input
            value={form.name}
            onChange={(e) => { setForm((p) => ({ ...p, name: e.target.value })); setErrors((p) => ({ ...p, name: "" })); }}
            placeholder="e.g. Request Demo"
            status={errors.name ? "error" : ""}
          />
          {errors.name && <div style={{ color: "#dc2626", fontSize: 12, marginTop: 5 }}>{errors.name}</div>}
        </div>

        <div>
          <label style={label}>Slug</label>
          <Input value={slugPreview} readOnly disabled placeholder="generated from the name" />
          <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 5, lineHeight: 1.5 }}>
            {isEdit
              ? "The slug cannot change — website forms and existing leads are wired to it."
              : "Generated from the name. This is the value a website form posts against."}
          </div>
        </div>

        <div>
          <label style={label}>Description</label>
          <Input.TextArea
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            rows={3}
            placeholder="What is this service for? (optional)"
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Switch
            checked={form.status === 1}
            onChange={(checked) => setForm((p) => ({ ...p, status: checked ? 1 : 0 }))}
          />
          <span style={{ fontSize: 13, fontWeight: 600, color: form.status === 1 ? "#16a34a" : "#6b7280" }}>
            {form.status === 1 ? "Active" : "Inactive"}
          </span>
        </div>
      </div>
    </Modal>
  );
};

/* ── Page ─────────────────────────────────────────────────────────────────── */
const ManageServices = () => {
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);   // service object, or {} to add

  const { data: services = [], isLoading, refetch, isFetching } = useServices();
  const toggleMutation = useToggleServiceStatus();
  const deleteMutation = useDeleteService();

  /* Writes are superadmin-only server-side; hiding the controls keeps a normal
     user from walking into a guaranteed 403. The toast still handles the case
     where the role changed since login. */
  const mayManage = isSuperAdmin();

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return services;
    return services.filter(
      (s) =>
        s.name?.toLowerCase().includes(q) ||
        s.slug?.toLowerCase().includes(q) ||
        s.description?.toLowerCase().includes(q),
    );
  }, [services, search]);

  const handleToggle = (service) => {
    const isActive = service.status === 1;
    Swal.fire({
      title: isActive ? "Deactivate this service?" : "Activate this service?",
      html: isActive
        ? `<b>${service.name}</b> will stop appearing in the services picker for new mappings.`
        : `<b>${service.name}</b> will be selectable again.`,
      icon: isActive ? "warning" : "question",
      showCancelButton: true,
      confirmButtonColor: isActive ? "#dc2626" : "#16a34a",
      cancelButtonColor: "#6b7280",
      confirmButtonText: isActive ? "Yes, deactivate" : "Yes, activate",
      reverseButtons: true,
    }).then((r) => {
      if (!r.isConfirmed) return;
      toggleMutation.mutate(service.id, {
        onSuccess: () =>
          Swal.fire({
            icon: "success",
            title: isActive ? "Deactivated" : "Activated",
            text: `${service.name} is now ${isActive ? "inactive" : "active"}.`,
            confirmButtonColor: "#5b48e8",
          }),
      });
    });
  };

  const handleDelete = (service) => {
    Swal.fire({
      title: `Delete "${service.name}"?`,
      html: `Leads already tagged to <b>${service.slug}</b> keep their reference, but no form can post against it again.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete",
      reverseButtons: true,
    }).then((r) => {
      if (r.isConfirmed) deleteMutation.mutate(service.id);
    });
  };

  const iconBtn = (color, border, bg) => ({
    width: 32, height: 32, borderRadius: 8, border: `1px solid ${border}`,
    background: bg, color, cursor: "pointer", display: "inline-flex",
    alignItems: "center", justifyContent: "center", fontSize: 13,
  });

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* Header */}
          <div style={{ background: "linear-gradient(135deg,#5b48e8 0%,#7c3aed 100%)", borderRadius: 16, padding: "28px 32px", marginBottom: 28, boxShadow: "0 8px 32px rgba(91,72,232,0.28)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 56, height: 56, background: "rgba(255,255,255,.22)", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, color: "white" }}>
                <AppstoreOutlined />
              </div>
              <div>
                <h4 style={{ color: "white", margin: 0, fontSize: 24, fontWeight: 700 }}>Manage Services</h4>
                <p style={{ color: "rgba(255,255,255,.85)", margin: 0, fontSize: 14 }}>
                  Lead sources the website forms post against
                </p>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Tooltip title="Refresh">
                <button
                  onClick={() => refetch()}
                  style={{ width: 40, height: 40, borderRadius: 10, border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.1)", color: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}
                >
                  <ReloadOutlined spin={isFetching} />
                </button>
              </Tooltip>
              {mayManage && (
                <button
                  onClick={() => setEditing({})}
                  style={{ display: "flex", alignItems: "center", gap: 7, height: 40, padding: "0 20px", borderRadius: 10, border: "none", background: "white", color: "#5b48e8", fontWeight: 700, fontSize: 14, cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
                >
                  <PlusOutlined /> Add Service
                </button>
              )}
            </div>
          </div>

          {/* Card */}
          <div style={{ background: "white", borderRadius: 16, border: "1px solid #f0f0f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, slug or description…"
                prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
                allowClear
                style={{ maxWidth: 340 }}
              />
              <span style={{ marginLeft: "auto", fontSize: 13, color: "#6b7280", fontWeight: 600 }}>
                {filtered.length} {filtered.length === 1 ? "service" : "services"}
              </span>
            </div>

            {isLoading ? (
              <div style={{ padding: 24 }}><Skeleton active paragraph={{ rows: 5 }} /></div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: "48px 20px" }}>
                <Empty
                  description={
                    search
                      ? "No service matches this search."
                      : "No services yet — add your first service."
                  }
                />
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead style={{ background: "#fafbff" }}>
                    <tr>
                      <th style={{ ...thStyle, width: 60 }}>#</th>
                      <th style={thStyle}>Name</th>
                      <th style={thStyle}>Slug</th>
                      <th style={thStyle}>Description</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Created</th>
                      {mayManage && <th style={{ ...thStyle, textAlign: "center" }}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((s, idx) => {
                      const isActive = s.status === 1;
                      return (
                        <tr key={s.id} style={{ borderTop: "1px solid #f1f5f9" }}>
                          <td style={{ ...tdStyle, color: "#9ca3af" }}>{idx + 1}</td>
                          <td style={{ ...tdStyle, fontWeight: 700, color: "#111827" }}>{s.name || "—"}</td>
                          <td style={tdStyle}>
                            {/* The exact string a form must post — monospace so it can be copied without doubt. */}
                            <span style={{ fontFamily: "monospace", fontSize: 12, background: "#f5f3ff", color: "#5b48e8", border: "1px solid #ddd6fe", borderRadius: 6, padding: "3px 9px", whiteSpace: "nowrap" }}>
                              {s.slug || "—"}
                            </span>
                          </td>
                          <td style={{ ...tdStyle, color: "#6b7280", maxWidth: 320 }}>
                            {s.description || <span style={{ color: "#d1d5db" }}>—</span>}
                          </td>
                          <td style={tdStyle}>
                            <span style={{ background: isActive ? "#f0fdf4" : "#f5f5f5", color: isActive ? "#16a34a" : "#6b7280", border: `1px solid ${isActive ? "#bbf7d0" : "#e5e7eb"}`, borderRadius: 20, padding: "3px 12px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
                              {isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td style={{ ...tdStyle, color: "#6b7280", whiteSpace: "nowrap" }}>{formatDate(s.created_at)}</td>
                          {mayManage && (
                            <td style={{ ...tdStyle, textAlign: "center" }}>
                              <div style={{ display: "inline-flex", gap: 6 }}>
                                <Tooltip title="Edit">
                                  <button onClick={() => setEditing(s)} style={iconBtn("#5b48e8", "#ddd6fe", "#f5f3ff")}>
                                    <EditOutlined />
                                  </button>
                                </Tooltip>
                                <Tooltip title={isActive ? "Deactivate" : "Activate"}>
                                  <button
                                    onClick={() => handleToggle(s)}
                                    disabled={toggleMutation.isPending}
                                    style={{
                                      ...iconBtn(
                                        isActive ? "#b45309" : "#16a34a",
                                        isActive ? "#fde68a" : "#bbf7d0",
                                        isActive ? "#fffbeb" : "#f0fdf4",
                                      ),
                                      cursor: toggleMutation.isPending ? "not-allowed" : "pointer",
                                    }}
                                  >
                                    {isActive ? <StopOutlined /> : <CheckCircleOutlined />}
                                  </button>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <button
                                    onClick={() => handleDelete(s)}
                                    disabled={deleteMutation.isPending}
                                    style={{
                                      ...iconBtn("#dc2626", "#fecaca", "#fef2f2"),
                                      cursor: deleteMutation.isPending ? "not-allowed" : "pointer",
                                    }}
                                  >
                                    <DeleteOutlined />
                                  </button>
                                </Tooltip>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {editing && (
        <ServiceFormModal
          service={editing.id ? editing : null}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
};

export default ManageServices;
